import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import Input from 'shared/components/input/Input';
import Select from 'shared/components/select/Select';
import Switch from 'shared/components/switch/Switch';
import { daysList, dayWeekList } from 'components/campaigns/CampaignConstants';
import { plusCircleAltIcon } from 'assets';
import { trashcanGreenIcon } from 'shared/assets';
import useStore from 'components/campaigns/hooks/useCampaignStore';
import CAMPAIGN_OBJECTIVE from 'enums/CampaignObjectiveEnum';
import { DAYS_OF_WEEK } from 'enums/DaysOfWeekEnum';
import { OpTimePicker } from 'op2mise-react-widgets';
import { getLastIdFromArray } from 'components/campaigns/helpers/helpers';
import { CUSTOM_DAYPART_ID } from 'components/campaigns/constants';

const DayPartFields = (props) => {
  const {
    dayPartErrors,
    dayPartList,
    dayPartRows,
    handleOnAddRows,
    handleOnAllocationChange,
    handleOnExcludeChange,
    handleOnKeyDownAddRows,
    handleOnKeyDownRemoveRows,
    handleOnChangeCustomDaypartName,
    handleOnRemoveRows,
    setDayPartRows,
    setValidDaypartFields,
  } = props;

  const { isCampaignActive, setIsFormTouched, selectedCampaign } = useStore(
    (state) => state
  );

  const customLabelRef = useRef(null);
  const radioType = 2;
  const isCampaignTypeRadio = selectedCampaign?.type === radioType;
  const { tvId, radioId } = CUSTOM_DAYPART_ID;
  const customDaypartId = isCampaignTypeRadio ? radioId : tvId;
  const isCampaignPlacedOrReach =
    selectedCampaign?.objective === CAMPAIGN_OBJECTIVE.placed ||
    selectedCampaign?.objective === CAMPAIGN_OBJECTIVE.reach;
  const timePickerFormat = 'HH:MM AM';

  let newDaypartList = dayPartList;
  const customDayparts = dayPartRows?.filter(
    (dpr) => !dayPartList.some((dpl) => dpl.id === dpr.daypartId)
  );
  const [daysOfWeekList, setDaysOfWeekList] = useState([]);

  // Manipulate the list and swap Prime and Su Prime index if campaign is TV
  if (!isCampaignTypeRadio) {
    const swapPrimeAndSuPrime = (dayparts) => {
      // Find the index (position) of each item in the array
      const primeIndex = dayparts.findIndex((dp) => dp.name === 'Prime');
      const suPrimeIndex = dayparts.findIndex((dp) => dp.name === 'Su Prime');

      // If we couldn't find both items, we can't swap them.
      // Return the original array to avoid errors.
      if (primeIndex === -1 || suPrimeIndex === -1) {
        return dayparts;
      }

      // Create a new array (shallow copy) to avoid mutating the original
      const newDayparts = [...dayparts];

      // The magic swap: use array destructuring to swap the elements at the two indices
      [newDayparts[primeIndex], newDayparts[suPrimeIndex]] = [
        newDayparts[suPrimeIndex],
        newDayparts[primeIndex],
      ];

      return newDayparts;
    };

    newDaypartList = swapPrimeAndSuPrime(newDaypartList);
  }
  // Group the dayparts by day or day range if campaign is Radio
  else {
    // Sort the list to group items by their day range in ascending chronological order.
    // The sorting criteria are:
    // 1. By the start day of the range (e.g., M-F comes before Sa).
    // 2. By the end day of the range (e.g., Sa comes before Sa-Su).
    // 3. By the start time within each day-range group.
    newDaypartList.sort((a, b) => {
      // Primary sort: by startDayId
      if (a.startDayId !== b.startDayId) {
        return a.startDayId - b.startDayId;
      }

      // Secondary sort: by endDayId
      // This correctly places single-day ranges before multi-day ranges starting on the same day.
      if (a.endDayId !== b.endDayId) {
        return a.endDayId - b.endDayId;
      }

      // Tertiary sort: by startTime for items within the same group.
      // localeCompare is a standard way to compare time strings like "HH:mm:ss".
      return a.startTime.localeCompare(b.startTime);
    });
  }

  const daypartListHalfCount = Math.ceil(newDaypartList.length / 2);
  const daypartListFirstHalf = newDaypartList.slice(0, daypartListHalfCount);
  const daypartListSecondHalf = newDaypartList.slice(daypartListHalfCount);

  const convertTime = (time) => {
    if (!time) return null;

    // set to zero if time is equivalent to 24 hours
    if (time === '1.00:00') time = '00:00';

    const [hours, minutes] = time.split(':');

    const currentDate = new Date();
    const combinedDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
      hours,
      minutes
    );

    return combinedDate;
  };

  const handleOnDayWeekSelect = (selectedId, selectedDay, propertyName) => {
    setIsFormTouched(true);

    setDayPartRows((prevRows) => {
      if (selectedId == null) {
        // get the last id or default to 0 if array is empty
        const lastId = getLastIdFromArray(prevRows);

        return [
          ...prevRows,
          {
            id: lastId + 1,
            daypartId: customDaypartId,
            [propertyName]: selectedDay.id,
          },
        ];
      }

      // update existing row
      return prevRows.map((row) =>
        row.id === selectedId
          ? {
              ...row,
              daypartId: customDaypartId,
              [propertyName]: selectedDay.id,
            }
          : row
      );
    });
  };

  const handleOnDayRangeSelect = (selectedId, selectedDayRange) => {
    setIsFormTouched(true);

    setDayPartRows((prevRows) => {
      if (selectedId == null) {
        // get the last id or default to 0 if array is empty
        const lastId = getLastIdFromArray(prevRows);

        return [
          ...prevRows,
          {
            id: lastId + 1,
            daypartId: customDaypartId,
            startDayId: selectedDayRange.startDayId,
            endDayId: selectedDayRange.endDayId,
            customDayRange: selectedDayRange.name,
          },
        ];
      }

      // update existing row
      return prevRows.map((row) =>
        row.id === selectedId
          ? {
              ...row,
              daypartId: customDaypartId,
              startDayId: selectedDayRange.startDayId,
              endDayId: selectedDayRange.endDayId,
              customDayRange: selectedDayRange.name,
            }
          : row
      );
    });
  };

  const handleOnChangeTime = (selectedId, data, name) => {
    const newTimeValue = moment(data.value).format('HH:mm');
    setIsFormTouched(true);

    setDayPartRows((prevRows) => {
      if (selectedId == null) {
        // get the last id or default to 0 if array is empty
        const lastId = getLastIdFromArray(prevRows);

        return [
          ...prevRows,
          {
            id: lastId + 1,
            daypartId: customDaypartId,
            [name]: newTimeValue,
          },
        ];
      }

      // update existing row
      return prevRows.map((row) =>
        row.id === selectedId
          ? { ...row, daypartId: customDaypartId, [name]: newTimeValue }
          : row
      );
    });
  };

  /*
   * To format the time to standard format while displaying the meridiem as 'a' or 'p'
   */
  const formatTime = (time) => {
  if (!time) return '';
  if (time.includes('1.00')) time = '24:00:00';

  const [hours, minutes] = time.split(':').map(Number);
  // Check for 24 first to set 'a', otherwise use standard logic
  const ampm = hours === 24 ? 'a' : (hours >= 12 ? 'p' : 'a');
  const adjustedHours = (((hours + 11) % 12) + 1).toString().padStart(2, '0');
  // Ensure minutes exist (handle potential undefined if split length is short)
  const safeMinutes = (minutes || 0).toString().padStart(2, '0');

  return `${adjustedHours}:${safeMinutes}${ampm}`;
};

  /*
   * To display the time range with this format '06:00a - 06:00p'
   */
  const displayTimeRange = (startTime, endTime) => {
    const formattedStartTime = formatTime(startTime);
    const formattedEndTime = formatTime(endTime);
    if (!formattedStartTime || !formattedEndTime) return '';
    return `${formattedStartTime} - ${formattedEndTime}`;
  };

  /*
   * To display the day range with this format
   * 'M - F' if day range is more than 1 day
   * 'M' if day range is just a single day
   */
  const displayDayRange = (startDayId, endDayId) => {
    const days = Object.values(DAYS_OF_WEEK);
    const startDay = days.find((d) => d.id === startDayId);
    const endDay = days.find((d) => d.id === endDayId);

    if (!startDay || !endDay) return '';

    // If start and end are the same, just return the single code
    if (startDayId === endDayId) {
      return startDay.code;
    }

    return `${startDay.code} - ${endDay.code}`;
  };

  const renderPredefinedDayparts = (predefinedDayparts) => {
    return (
      <>
        <div className={'row w-100 mb-3'}>
          <label className={'col'}>Name</label>
          <label className={'col'}>Time</label>
          <label className={'col-2'}>Day</label>
          <label className={'col-2 text-center'}>%</label>
          <label className={'col-1'}>Exclude</label>
        </div>
        {predefinedDayparts.map((daypart) => {
          const daypartDistribution =
            dayPartRows?.find((dpr) => dpr.daypartId === daypart.id) || null;
          return (
            <div
              className={'row w-100 mb-2'}
              key={`campaign-form-daypart-fields-${daypart.id}`}
            >
              <label className={'col day-week-rows hide-text-overflow'}>
                <b>{daypart.name}</b>
              </label>
              <span className={'col day-week-rows hide-text-overflow'}>
                {displayTimeRange(daypart.startTime, daypart.endTime)}
              </span>
              <span className={'col-2 day-week-rows hide-text-overflow'}>
                {displayDayRange(daypart.startDayId, daypart.endDayId)}
              </span>
              <div className={'col-2'}>
                <Input
                  disabled={
                    daypartDistribution?.exclude ||
                    !isCampaignActive ||
                    isCampaignPlacedOrReach
                  }
                  maxLength={5}
                  width={'100%'}
                  onChange={(e) =>
                    handleOnAllocationChange(
                      'DAYPART',
                      daypartDistribution?.id ?? null,
                      e,
                      daypart
                    )
                  }
                  style={{ color: '#212529' }}
                  value={
                    !daypartDistribution?.exclude
                      ? daypartDistribution?.allocation
                      : ''
                  }
                />
              </div>
              <div className={'col-1 day-week-rows'}>
                <div className={'exclude-switch-container'}>
                  <Switch
                    active={daypartDistribution?.exclude ?? false}
                    disabled={!isCampaignActive}
                    onSwitch={(isActive) => {
                      handleOnExcludeChange(
                        'DAYPART',
                        daypartDistribution?.id ?? null,
                        isActive,
                        daypart
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const renderDayRangeSelector = (rowData) => {
    const { id, startDayId, endDayId } = rowData;

    if (isCampaignTypeRadio) {
      const dayRange =
        daysOfWeekList?.find(
          (item) => item.startDayId === startDayId && item.endDayId === endDayId
        ) || null;

      return (
        <Select
          text={dayRange?.name || 'Please Select'}
          list={daysOfWeekList}
          noSearch
          onSelect={(e) => handleOnDayRangeSelect(id, e)}
          width={'150px'}
          disabled={!isCampaignActive}
        />
      );
    } else {
      const startDay =
        daysOfWeekList?.find((item) => item.id === startDayId) || null;
      const endDay =
        daysOfWeekList?.find((item) => item.id === endDayId) || null;
      return (
        <>
          <div style={{ width: '100%', maxWidth: '150px' }}>
            <Select
              text={startDay?.name || 'Please Select'}
              list={daysOfWeekList}
              noSearch
              onSelect={(e) => handleOnDayWeekSelect(id, e, 'startDayId')}
              width={'100%'}
              disabled={!isCampaignActive}
            />
          </div>
          <span> to </span>
          <div style={{ width: '100%', maxWidth: '150px' }}>
            <Select
              text={endDay?.name || 'Please Select'}
              list={daysOfWeekList}
              noSearch
              onSelect={(e) => handleOnDayWeekSelect(id, e, 'endDayId')}
              width={'100%'}
              disabled={!isCampaignActive}
            />
          </div>
        </>
      );
    }
  };

  const handleValidateInput = (isValid, id, type) => {
    setValidDaypartFields(prev => {
      const prevEntry = prev[id] ?? {};

      // if value is already the same, return prev (no state change)
      if (prevEntry[type] === isValid) return prev;

      prev = {
        ...prev,
        [id]: {
          ...prevEntry,
          [type]: isValid,
        },
      };

      return prev;
    });
  };

  useEffect(() => {
    if (isCampaignTypeRadio) setDaysOfWeekList(daysList);
    else
      setDaysOfWeekList(
        dayWeekList.map((item) => ({ ...item, id: item.id + 1 }))
      );
  }, []);

  return (
    <>
      <div>
        <div className="w-100 border-bottom d-flex gap-2">
          <span>Daypart</span>
        </div>
      </div>

      <div className={'row'}>
        <div className={'col-6'} style={{ borderRight: '1px solid #E2E8F0' }}>
          {renderPredefinedDayparts(daypartListFirstHalf)}
        </div>
        <div className={'col-6'}>
          {renderPredefinedDayparts(daypartListSecondHalf)}
        </div>
      </div>

      <div className="w-100 d-flex flex-column gap-2">
        {customDayparts?.map((dayWeekRow, rowIndex) => {
          const {
            id,
            customDaypartName,
            allocation,
            startTime,
            endTime,
            exclude,
          } = dayWeekRow;
          const startTimeStamp = convertTime(startTime);
          const endTimeStamp = convertTime(endTime);

          return (
            <div key={`dayPartRow${id}`}>
              <div className="day-week-rows">
                <label
                  ref={rowIndex === 0 ? customLabelRef : null}
                  style={{ marginRight: '9.5%' }}
                >
                  <b>Custom</b>
                </label>
                <Input
                  value={customDaypartName}
                  onChange={(e) =>
                    handleOnChangeCustomDaypartName('DAYPART', id, e)
                  }
                  style={{ width: '100%', maxWidth: '120px', color: '#212529' }}
                  disabled={!isCampaignActive || isCampaignPlacedOrReach}
                  placeholder={'Name'}
                />
                <OpTimePicker
                  enabled={!(!isCampaignActive || isCampaignPlacedOrReach)}
                  onChange={(params) =>
                    handleOnChangeTime(id, params, 'startTime')
                  }
                  placeholder={timePickerFormat}
                  width={'120px'}
                  validateInput={(isValid) => handleValidateInput(isValid, id, 'startTime')}
                  value={startTimeStamp}
                />
                <span> to </span>
                <OpTimePicker
                  enabled={!(!isCampaignActive || isCampaignPlacedOrReach)}
                  onChange={(params) =>
                    handleOnChangeTime(id, params, 'endTime')
                  }
                  placeholder={timePickerFormat}
                  width={'120px'}
                  validateInput={(isValid) => handleValidateInput(isValid, id, 'endTime')}
                  value={endTimeStamp}
                />
                {renderDayRangeSelector(dayWeekRow)}
                <Input
                  value={allocation || ''}
                  maxLength={5}
                  onChange={(e) =>
                    handleOnAllocationChange('DAYPART', id, e, {
                      id: customDaypartId,
                    })
                  }
                  style={{ width: '100%', maxWidth: '90px', color: '#212529' }}
                  disabled={
                    exclude || !isCampaignActive || isCampaignPlacedOrReach
                  }
                />
                <div className="exclude-switch-container">
                  <Switch
                    active={exclude}
                    disabled={!isCampaignActive}
                    title={'Exclude'}
                    onSwitch={(isActive) => {
                      handleOnExcludeChange('DAYPART', id, isActive, {
                        id: customDaypartId,
                      });
                    }}
                  />
                </div>
                {customDayparts.length > 1 && (
                  <img
                    src={trashcanGreenIcon}
                    alt="trashcanGreenIcon"
                    title={
                      isCampaignActive
                        ? 'Delete daypart distribution'
                        : undefined
                    }
                    className={`action-icon ${
                      !isCampaignActive ? 'disabled' : ''
                    }`}
                    onClick={() => handleOnRemoveRows('DAYPART', id)}
                    onKeyDown={(e) =>
                      handleOnKeyDownRemoveRows('DAYPART', id, e)
                    }
                    tabIndex={isCampaignActive ? 0 : -1}
                  />
                )}
                {customDayparts.length - 1 === rowIndex && (
                  <img
                    src={plusCircleAltIcon}
                    alt="plusCircleAltIcon"
                    title={
                      isCampaignActive ? 'Add daypart distribution' : undefined
                    }
                    className={`action-icon ${
                      !isCampaignActive ? 'disabled' : ''
                    }`}
                    onClick={() => handleOnAddRows('DAYPART')}
                    onKeyDown={(e) => handleOnKeyDownAddRows('DAYPART', e)}
                    tabIndex={isCampaignActive ? 0 : -1}
                  />
                )}
              </div>
              {dayPartErrors[id] && (
                <div className="d-flex gap-2 day-week-error-rows">
                  {/* Added blank label to align error message to input fields, 9 is only a filler width adjust if needed */}
                  <label
                    style={{
                      width: customLabelRef.current.clientWidth + 9,
                      marginRight: '9.5%',
                    }}
                  ></label>
                  <p className="w-100 is-danger capitalize">
                    <span>{dayPartErrors[id]?.daypart}</span>
                    <span>{dayPartErrors[id]?.timeRange}</span>
                    <span>{dayPartErrors[id]?.allocation}</span>
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default DayPartFields;
